import React, { FC, useEffect, useState } from 'react';

import {
  SettingOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  FacebookOutlined,
} from '@ant-design/icons';
import { Flex, Layout, Menu, Popover, Row, Switch, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import { useDebounce } from 'hooks/useDebounce';
import { useDevice } from 'hooks/useDevice';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'services/redux/configureStore';
import { isMainMenuCollapsed, setCurrentNavigationRoute } from 'services/redux/navigation/actions';
import { switchTheme } from 'services/redux/theme/actions';
import { storeApi } from 'services/rtk/store';
import { usersApi } from 'services/rtk/users';
import { width } from 'utils/helpers/width';

import styles from './index.module.css';
import scrollbarStyles from './scroll.module.css';
import { NotificationsWidget } from '../components/NotificationsWIdget/NotificationsWidget';
import { useMenu } from '../model';
import { ThemeSwitcher } from './theme-switcher';
import { getCompanyLogo } from '../lib/companies';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

const MainLayout: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentLocation = useLocation().pathname;

  const [mobileMenuVisible, setMobileMenuVisible] = useState(true);

  const sidebarMenuCollapsed = useAppSelector((state) => state.navigation.sidebarMenuCollapsed);
  const isDarkMode = useAppSelector((state) => state.theme.isDarkMode);

  const { data: me, isError } = usersApi.useFetchMeQuery();
  const [fetchShopCategories, result] = storeApi.useLazyFetchProductCategoriesQuery();
  const categories = result.isSuccess ? result.data : [];

  const isMobile = useDevice();

  const logo = getCompanyLogo();

  const handleResize = () => {
    if (window.innerWidth < 1200) {
      dispatch(isMainMenuCollapsed(true));
    }
  };

  const debounced = useDebounce(handleResize, 150);

  useEffect(() => {
    window.addEventListener('resize', debounced);
    return () => window.removeEventListener('resize', debounced);
  });

  useEffect(() => {
    if (width < 1200) {
      dispatch(isMainMenuCollapsed(true));
    }
    if (width < 820) {
      setMobileMenuVisible(false);
    }
  }, [width]);

  useEffect(() => {
    if (width <= 820) {
      setMobileMenuVisible(false);
    }
  }, [currentLocation]);

  const { selectedKeys, openKeys, items } = useMenu(currentLocation, me, categories);

  useEffect(() => {
    if (currentLocation.startsWith('/store')) fetchShopCategories(undefined, true);
  }, [fetchShopCategories, currentLocation]);

  useEffect(() => {
    document.body.classList.toggle(scrollbarStyles.dark, isDarkMode);
  }, [isDarkMode]);

  if (isError) {
    return <Navigate to={'/login'} />;
  }

  if (!me) return null;

  return (
    <Layout className={styles.wrapper} data-testid="layout">
      <Header className={styles.header}>
        <Row justify="space-between" className={styles.header__mobile}>
          <div className={styles.menu}>
            {width < 820 && (
              <>
                {mobileMenuVisible ? (
                  <MenuUnfoldOutlined
                    className={styles.burger}
                    onClick={() => {
                      setMobileMenuVisible(false);
                    }}
                  />
                ) : (
                  <MenuFoldOutlined
                    className={styles.burger}
                    onClick={() => {
                      setMobileMenuVisible(true);
                    }}
                  />
                )}
              </>
            )}
            <img src={logo.icon} alt="logo" width={logo.width} height={logo.height} />
          </div>
          <Row className={styles.settings}>
            {!['HR', 'NEW_USER'].includes(me.role) && (
              <Tooltip title={width < 820 ? undefined : 'FB manager'} mouseEnterDelay={0.4} zIndex={9999}>
                <FacebookOutlined
                  id="facebook-icon"
                  className={styles.settings__icon}
                  onClick={() => navigate('/fbmanager/main')}
                />
              </Tooltip>
            )}

            {(me.role === 'ADMIN' || me.role === 'TEAM_LEAD') && (
              <Tooltip title={width < 820 ? undefined : 'Трекеры'} mouseEnterDelay={0.4} zIndex={9999}>
                <SettingOutlined
                  id="tracker-icon"
                  className={styles.settings__icon}
                  onClick={() => {
                    navigate('/trackers');
                    dispatch(setCurrentNavigationRoute('/trackers'));
                  }}
                />
              </Tooltip>
            )}

            {!['HR', 'NEW_USER'].includes(me.role) && <NotificationsWidget currentUser={me} />}

            {width > 500 ? (
              <Link to="/me" id="header-link-to-me">
                <Tooltip title={width < 820 ? undefined : 'Мой профиль'} mouseEnterDelay={0.4} zIndex={9999}>
                  <Text className={styles.settings__email} style={{ color: 'var(--light-gray)' }}>
                    {me?.email}
                  </Text>
                </Tooltip>
              </Link>
            ) : (
              <UserOutlined
                onClick={() => {
                  navigate('/me');
                  dispatch(setCurrentNavigationRoute('/me'));
                }}
                className={styles.settings__icon}
                style={{ color: '#fff' }}
              />
            )}
            {width < 820 && (
              <Popover
                trigger="click"
                zIndex={9999}
                content={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      columnGap: 8,
                      alignItems: 'center',
                      color: isDarkMode ? '#fff' : '#000',
                      left: width > 1367 ? 110 : 80,
                    }}
                  >
                    <MdLightMode />
                    <Switch checked={isDarkMode} onChange={() => dispatch(switchTheme(!isDarkMode))} />
                    <MdDarkMode />
                  </div>
                }
              >
                <Flex align={'center'}>
                  {isDarkMode ? (
                    <MdDarkMode style={{ cursor: 'pointer', width: 18, height: 18 }} />
                  ) : (
                    <MdLightMode style={{ color: '#fff', cursor: 'pointer', width: 18, height: 18 }} />
                  )}
                </Flex>
              </Popover>
            )}
          </Row>
        </Row>
      </Header>
      <Layout className={cn(styles.container)}>
        <div className={`${styles.menu__bg} ${isDarkMode ? styles.menu__bg_dark : ''}`}>
          <Sider
            width={width > 1367 ? 300 : 265}
            collapsible
            defaultCollapsed={width < 1200 && true}
            collapsed={width <= 820 ? false : sidebarMenuCollapsed}
            onCollapse={() => dispatch(isMainMenuCollapsed(!sidebarMenuCollapsed))}
            className={`${styles.sidebar__menu} ${isDarkMode ? styles.sider_dark : ''}`}
            style={{
              display: mobileMenuVisible ? 'block' : 'none',
              background: isDarkMode ? '#141414' : '#fff',
              position: 'sticky',
              top: 0,
              height: 'calc(100vh - 64px)',
              overflow: 'auto',
              scrollbarWidth: 'none',
              userSelect: 'none',
            }}
          >
            <Menu
              mode="inline"
              onSelect={() => {
                if (width < 820) {
                  setMobileMenuVisible(true);
                }
              }}
              items={items}
              selectedKeys={selectedKeys}
              onClick={() => dispatch(setCurrentNavigationRoute(window.location.pathname))}
              defaultOpenKeys={
                width <= 820
                  ? currentLocation.startsWith('/fbmanager')
                    ? [...openKeys, '1']
                    : [...openKeys, '0']
                  : sidebarMenuCollapsed
                    ? []
                    : currentLocation.startsWith('/fbmanager')
                      ? [...openKeys, '1']
                      : [...openKeys, '0']
              }
            />
          </Sider>
        </div>

        <Layout style={{ marginLeft: isMobile ? 0 : undefined }} className={styles.layout}>
          <Content
            className={cn(styles.content, styles['site-layout-background'])}
            style={{ background: isDarkMode ? '#1f1f1f' : '#fff' }}
          >
            <Outlet context={me} />
          </Content>
        </Layout>

        <ThemeSwitcher />
      </Layout>
    </Layout>
  );
};

export default MainLayout;
export * from './menu-links';
